/* eslint-disable security/detect-object-injection */
import React, {JSX, useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Typography, Tab, Tabs} from '@mui/material';
import {t} from 'i18next';
import ArrTreeView from '../../common/arr-tree-view/ArrTreeView';
import ArrButton from '../../common/arr-button/ArrButton';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import DragAndDropSelectedList from './drag-drop/DragAndDropSelectedList';
import {Items} from './drag-drop/DragAndDropItems';
import {ToastType} from '../../../shared/constants';
import {useDocListMetaData} from '../../../hooks/document/useDocument';
import {useDispatch} from 'react-redux';
import {setShowManageColumns} from '../../../redux/slice/docListSlice';
import './ManageColumns.scss';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../shared/interfaces';
import {ITreeMetadata} from '../../../api/types/document.type';
import ArrRadio from '../../common/arr-radio/ArrRadio';
import {IDynamicColDef} from '../DocList';
import ArrAccordion from '../../common/arr-accordion/ArrAccordion';
import ArrTooltip from '../../common/arr-tooltip/ArrTooltip';

type CategoryKey =
  | 'metadata'
  | 'production'
  | 'tags'
  | 'redactionTags'
  | 'commentType'
  | 'folders'
  | 'score'
  | 'ai';

const getToolTip = () => {
  return t('docList.manageColumn.scoreTooltip');
};

const a11yProps = (index: number) => ({
  id: `simple-tab-${index}`,
  'aria-controls': `simple-tabpanel-${index}`,
  name: `simple-tab-${index}`,
  'data-testid': `simple-tab-${index}`,
});

const MAX_TOTAL_SELECTED = 10;

const buildTree = (
  items: ITreeMetadata[] = [],
  rootParentId = '#',
  category = ''
) => {
  const nodeMap: any = {};
  items.forEach((it: ITreeMetadata) => {
    nodeMap[it.id] = {
      ...it,
      label: it.text,
      id: category ? `${category}-${it.id}` : it.id,
      skipParent: it.parent == '#' || (category === 'SCORE' && it.id === '2g'),
    };
    delete nodeMap[it.id].children;
  });

  const roots: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];
  items.forEach((it: ITreeMetadata) => {
    const node = nodeMap[it.id];
    const parentId = it.parent;
    if (!parentId || parentId === rootParentId || !nodeMap[parentId])
      roots.push(node);
    else {
      const parent = nodeMap[parentId];
      if (!parent.children) parent.children = [];
      parent.children.push(node);
    }
  });

  Object.values(nodeMap).forEach((n: any) => {
    n.hasChildren = Array.isArray(n.children) && n.children.length > 0;
  });

  return roots;
};

interface ManageColumnsProps {
  currentColumns: IDynamicColDef[];
  onSave?: (updatedSelected: IDynamicColDef[]) => void;
}

const ManageColumns: React.FC<ManageColumnsProps> = ({
  currentColumns,
  onSave,
}) => {
  const categories: {key: CategoryKey; label: string; icon?: JSX.Element}[] = [
    {key: 'metadata', label: 'Metadata'},
    {key: 'tags', label: 'Tags'},
    {key: 'redactionTags', label: 'Redaction Tags'},
    {key: 'commentType', label: 'Comments'},
    {key: 'folders', label: 'Folders'},
    {
      key: 'score',
      label: 'Scores',
      icon: (
        <Box onClick={(e) => e.stopPropagation()}>
          <ArrTooltip trigger="click" content={getToolTip()} />
        </Box>
      ),
    },
    {key: 'production', label: 'Production'},
    {key: 'ai', label: 'AI'},
  ];

  const dispatch = useDispatch();
  const {data: colData, isLoading} = useDocListMetaData();

  const [currentTab, setCurrentTab] = useState<number>(0);
  const [isShowToast, setIsShowToast] = useState(false);

  const [selectedByCategory, setSelectedByCategory] = useState<
    Record<CategoryKey, Items[]>
  >({
    metadata: [],
    production: [],
    tags: [],
    redactionTags: [],
    commentType: [],
    folders: [],
    score: [],
    ai: [],
  });

  const [pendingByCategory, setPendingByCategory] = useState<
    Record<CategoryKey, string[]>
  >({
    metadata: [],
    production: [],
    tags: [],
    redactionTags: [],
    commentType: [],
    folders: [],
    score: [],
    ai: [],
  });

  const seqRef = React.useRef<number>(0);
  const tabsRef = React.useRef<HTMLDivElement | null>(null);
  const availableHeadingRef = React.useRef<HTMLHeadingElement>(null);
  const addToSelectedRef = React.useRef<HTMLButtonElement>(null);
  const selectedHeadingRef = React.useRef<HTMLHeadingElement>(null);
  const maxColumnsRef = React.useRef<HTMLParagraphElement>(null);
  const availableContentRef = React.useRef<HTMLDivElement | null>(null);

  const makeDisplayId = (id: string, category: CategoryKey) => {
    if (category !== 'tags') return id;
    const node = tagNodeMap.get(id);
    const isGroup =
      !!node && Array.isArray(node.children) && node.children.length > 0;
    const prefix = isGroup ? 'TAGGROUP-' : 'TAG-';
    return `${prefix}${id}`;
  };

  useEffect(() => {
    const allSeqs = (Object.keys(selectedByCategory) as CategoryKey[]).flatMap(
      (c) => (selectedByCategory[c] ?? []).map((it) => Number(it.sequence ?? 0))
    );
    const maxSeq = allSeqs.length ? Math.max(...allSeqs) : 0;
    seqRef.current = Math.max(seqRef.current, maxSeq);
  }, []);

  const toIdString = (id: string) =>
    id === null || id === undefined ? '' : String(id);

  const getGlobalSelectedSet = (selByCat: Record<CategoryKey, Items[]>) => {
    const s = new Set<string>();
    (Object.keys(selByCat) as CategoryKey[]).forEach((cat) =>
      (selByCat[cat] ?? []).forEach((it) => s.add(toIdString(it.id)))
    );
    return s;
  };

  const dedupePreserveOrder = (arr: string[]) => {
    const seen = new Set<string>();
    const out: string[] = [];
    arr.forEach((id) => {
      const key = toIdString(id);
      if (!seen.has(key)) {
        seen.add(key);
        out.push(key);
      }
    });
    return out;
  };

  const [confirmTagModalOpen, setConfirmTagModalOpen] = useState(false);
  const [confirmModalPendingCategory, setConfirmModalPendingCategory] =
    useState<CategoryKey | null>(null);
  const [confirmModalParentOptions, setConfirmModalParentOptions] = useState<
    {id: string; label: string; choice: 'group' | 'individual'}[]
  >([]);

  const metadataList = useMemo(
    () =>
      (colData?.metadata ?? []).map((it) => ({
        id: 'FIELD-' + it.fieldID,
        label: it.fieldLabel,
        dataType: it.fieldDataTye,
      })),
    [colData?.metadata]
  );

  const productionList = useMemo(
    () =>
      (colData?.production ?? []).map((it) => ({
        id: 'PRODUCTION-' + it.fieldID,
        label: it.fieldLabel,
        dataType: it.fieldDataTye,
      })),
    [colData?.production]
  );

  const tagTree = useMemo(
    () => buildTree(colData?.tagList ?? [], '#', 'TAG'),
    [colData?.tagList]
  );
  const redactionTree = useMemo(
    () => buildTree(colData?.redactionTags ?? [], '#', 'REDACTION'),
    [colData?.redactionTags]
  );
  const commentTypeTree = useMemo(
    () => buildTree(colData?.comments ?? [], '#', 'COMMENT'),
    [colData?.comments]
  );
  const folderTree = useMemo(
    () => buildTree(colData?.folderList ?? [], '#', 'FOLDER'),
    [colData?.folderList]
  );
  const scoreTree = useMemo(
    () => buildTree(colData?.scoreList ?? [], '#', 'SCORE'),
    [colData?.folderList]
  );
  const aiTree = useMemo(
    () =>
      buildTree(colData?.aiGeneratedSummary ?? [], '#', 'AIGENERATEDSUMMARY'),
    [colData?.folderList]
  );

  const labelMaps = useMemo(() => {
    const makeMap = (nodes: any[] = []) => {
      const m = new Map<string, string>();
      const walk = (arr: any[] = []) => {
        arr.forEach((n) => {
          m.set(
            n.id?.toString() ?? '',
            n.label ?? n.text ?? n.name ?? n.id?.toString() ?? ''
          );
          if (n.children) walk(n.children);
        });
      };
      walk(nodes);
      return m;
    };

    const mp = new Map<CategoryKey, Map<string, string>>();
    mp.set('metadata', makeMap(metadataList));
    mp.set('production', makeMap(productionList));
    mp.set('tags', makeMap(tagTree));
    mp.set('redactionTags', makeMap(redactionTree));
    mp.set('commentType', makeMap(commentTypeTree));
    mp.set('folders', makeMap(folderTree));
    mp.set('score', makeMap(scoreTree));
    mp.set('ai', makeMap(aiTree));
    return mp;
  }, [
    metadataList,
    productionList,
    tagTree,
    redactionTree,
    commentTypeTree,
    folderTree,
    scoreTree,
    aiTree,
  ]);

  const tagNodeMap = useMemo(() => {
    const m = new Map<string, any>();
    const walk = (arr: any[] = []) => {
      arr.forEach((n) => {
        m.set(n.id?.toString() ?? '', n);
        if (n.children) walk(n.children);
      });
    };
    walk(tagTree);
    return m;
  }, [tagTree]);

  const collectDescendants = useCallback(
    (nodeId: string, nodeMap: Map<string, any>) => {
      const result: string[] = [];
      const node = nodeMap.get(nodeId);
      if (!node) return result;
      const walk = (n: any) => {
        result.push(n.id?.toString() ?? '');
        if (n.children) {
          n.children.forEach((c: any) => walk(c));
        }
      };
      walk(node);
      return result;
    },
    []
  );

  const globalSelectedIds = useMemo(() => {
    const s = new Set<string>();
    (Object.keys(selectedByCategory) as CategoryKey[]).forEach((cat) => {
      selectedByCategory[cat].forEach((it) => s.add(it.id.toString()));
    });
    return s;
  }, [selectedByCategory]);

  const inferCategoryFromId = useCallback(
    (id: string): CategoryKey | undefined => {
      if (!id) return undefined;
      const low = id.toString();
      if (low.startsWith('FIELD-')) return 'metadata';
      if (low.startsWith('PRODUCTION-')) return 'production';
      if (low.startsWith('TAG-')) return 'tags';
      if (low.startsWith('REDACTION-')) return 'redactionTags';
      if (low.startsWith('COMMENT-')) return 'commentType';
      if (low.startsWith('FOLDER-')) return 'folders';
      if (low.startsWith('SCORE-')) return 'score';
      if (low.startsWith('AI-')) return 'ai';
      for (const cat of [
        'metadata',
        'production',
        'tags',
        'redactionTags',
        'commentType',
        'folders',
        'score',
        'ai',
      ] as CategoryKey[]) {
        const map = labelMaps.get(cat);
        if (map?.has(id)) return cat;
      }
      return undefined;
    },
    [labelMaps]
  );

  useEffect(() => {
    if (
      !currentColumns ||
      !Array.isArray(currentColumns) ||
      currentColumns.length === 0
    )
      return;

    let curSeq = Number(seqRef.current ?? 0);

    const buckets: Record<CategoryKey, Items[]> = {
      metadata: [],
      production: [],
      tags: [],
      redactionTags: [],
      commentType: [],
      folders: [],
      score: [],
      ai: [],
    };

    currentColumns.forEach((col) => {
      let id: string | undefined;
      let label: string | undefined;

      if (typeof col === 'string') {
        id = col;
      } else if (col && typeof col === 'object') {
        if (col.FieldID && col.FieldType) {
          id = `${col.FieldType}-${col.FieldID}` as string;
        }
        if (col.FieldLabel) label = col.FieldLabel;
      }

      if (!id) return;

      const cat = inferCategoryFromId(id) ?? 'metadata';
      const resolvedLabel = label ?? labelMaps.get(cat)?.get(id) ?? id;

      if (buckets[cat].some((x) => toIdString(x.id) === id)) return;

      let assignedSequence: number;
      if (
        typeof col === 'object' &&
        col != null &&
        (col.SequenceNumber || col.SequenceNumber === 0)
      ) {
        assignedSequence = Number(col.SequenceNumber);
        if (assignedSequence > curSeq) curSeq = assignedSequence;
      } else {
        curSeq += 1;
        assignedSequence = curSeq;
      }

      buckets[cat].push({
        id,
        label: resolvedLabel,
        sequence: assignedSequence,
        displayId: makeDisplayId(id, cat),
      } as Items & {displayId?: string});
    });

    const flattened = (Object.keys(buckets) as CategoryKey[]).flatMap((c) =>
      (buckets[c] ?? []).map(
        (it) =>
          ({
            ...it,
            __category: c,
          }) as Items & {__category: CategoryKey}
      )
    );
    flattened.sort(
      (a, b) =>
        Number(a.sequence ?? Number.MAX_SAFE_INTEGER) -
        Number(b.sequence ?? Number.MAX_SAFE_INTEGER)
    );
    const rebuilt = rebuildBucketsFromGlobal(flattened);

    setSelectedByCategory(rebuilt);
    seqRef.current = curSeq;

    setPendingByCategory((prev) => {
      const next: Record<CategoryKey, string[]> = {...prev};
      (Object.keys(next) as CategoryKey[]).forEach((cat) => {
        const selectedIds = (rebuilt[cat] ?? []).map((s) => toIdString(s.id));
        next[cat] = prev[cat].filter(
          (id) => !selectedIds.includes(toIdString(id))
        );
      });
      return next;
    });
  }, [currentColumns, labelMaps, inferCategoryFromId]);

  const handleTreeSelection = useCallback(
    (category: CategoryKey) => (values: string[] | null) => {
      const filtered = (values ?? []).filter(
        (id) => !globalSelectedIds.has(id)
      );
      setPendingByCategory((prev) => ({...prev, [category]: filtered}));
    },
    [globalSelectedIds]
  );

  const getGlobalMaxSequence = (selByCat: Record<CategoryKey, Items[]>) => {
    const allSeqs = (Object.keys(selByCat) as CategoryKey[]).flatMap((c) =>
      (selByCat[c] ?? []).map((it) => Number(it.sequence ?? 0))
    );
    return allSeqs.length ? Math.max(...allSeqs) : 0;
  };

  const emptyBuckets = (): Record<CategoryKey, Items[]> => ({
    metadata: [],
    production: [],
    tags: [],
    redactionTags: [],
    commentType: [],
    folders: [],
    score: [],
    ai: [],
  });

  const rebuildBucketsFromGlobal = (
    globalList: (Items & {__category: CategoryKey})[]
  ) => {
    const newBuckets = emptyBuckets();

    globalList.sort(
      (a, b) =>
        Number(a.sequence ?? Number.MAX_SAFE_INTEGER) -
        Number(b.sequence ?? Number.MAX_SAFE_INTEGER)
    );
    globalList.forEach((it) => {
      const cat = it.__category ?? 'metadata';
      newBuckets[cat].push(it);
    });
    return newBuckets;
  };

  const addPendingToSelected = useCallback(
    (category: CategoryKey) => {
      const pendingRaw = pendingByCategory[category] ?? [];
      if (!pendingRaw || pendingRaw.length === 0) return;
      const pendingIds = dedupePreserveOrder(pendingRaw.map(toIdString));
      if (pendingIds.length === 0) return;

      if (category === 'tags') {
        const parentPending = pendingIds.filter((id) => {
          const node = tagNodeMap.get(id);
          return (
            !!node && Array.isArray(node.children) && node.children.length > 0
          );
        });

        if (parentPending.length > 0) {
          const parentOptions = parentPending.map((id) => ({
            id,
            label: (labelMaps.get('tags')?.get(id) ?? id) as string,
            choice: 'group' as 'group' | 'individual',
          }));
          setConfirmModalParentOptions(parentOptions);
          setConfirmModalPendingCategory('tags');

          setConfirmTagModalOpen(true);
          return;
        }
      }

      const globalSelected = getGlobalSelectedSet(selectedByCategory);
      const uniquePending = pendingIds.filter((id) => !globalSelected.has(id));
      if (uniquePending.length === 0) {
        setPendingByCategory((prev) => ({...prev, [category]: []}));
        return;
      }

      const totalSelectedBefore = Object.values(selectedByCategory).reduce(
        (acc, arr) => acc + arr.length,
        0
      );
      const availableSlots = MAX_TOTAL_SELECTED - totalSelectedBefore;
      if (availableSlots <= 0) {
        setIsShowToast(true);
        return;
      }

      const toAdd = uniquePending.slice(0, availableSlots);
      const remaining = uniquePending.slice(availableSlots);

      const maxSeq = getGlobalMaxSequence(selectedByCategory);
      let cur = maxSeq;
      const addedItems: (Items & {__category: CategoryKey})[] = toAdd.map(
        (id) => {
          cur += 1;
          const label = labelMaps.get(category)?.get(id) ?? id;
          return {
            id,
            label,
            sequence: cur,
            __category: category,
            displayId: makeDisplayId(id, category),
          } as Items & {__category: CategoryKey; displayId?: string};
        }
      );

      const currentGlobal = (
        Object.keys(selectedByCategory) as CategoryKey[]
      ).flatMap((c) =>
        (selectedByCategory[c] ?? []).map(
          (it) =>
            ({
              ...it,
              __category: c,
            }) as Items & {__category: CategoryKey}
        )
      );

      const combined = currentGlobal.concat(addedItems);
      const rebuilt = rebuildBucketsFromGlobal(combined);

      setSelectedByCategory(rebuilt);

      seqRef.current = cur;

      setPendingByCategory((prev) => ({...prev, [category]: remaining}));
      if (remaining.length > 0) setIsShowToast(true);
    },
    [
      pendingByCategory,
      selectedByCategory,
      tagNodeMap,
      labelMaps,
      MAX_TOTAL_SELECTED,
      setSelectedByCategory,
      setPendingByCategory,
      setIsShowToast,
      setConfirmModalParentOptions,
      setConfirmModalPendingCategory,
      setConfirmTagModalOpen,
    ]
  );

  const handleConfirmTagModalPerParent = useCallback(() => {
    if (!confirmModalPendingCategory) {
      setConfirmTagModalOpen(false);
      return;
    }
    const cat = confirmModalPendingCategory;
    const pendingRaw = pendingByCategory[cat] ?? [];
    const pendingIds = dedupePreserveOrder(pendingRaw.map(toIdString));
    if (pendingIds.length === 0) {
      setConfirmTagModalOpen(false);
      return;
    }

    const globalSelected = getGlobalSelectedSet(selectedByCategory);

    const parentsGroupSet = new Set(
      confirmModalParentOptions
        .filter((p) => p.choice === 'group')
        .map((p) => p.id)
    );
    const parentsIndividualSet = new Set(
      confirmModalParentOptions
        .filter((p) => p.choice === 'individual')
        .map((p) => p.id)
    );

    const descendantsOfGroupParents = new Set<string>();
    parentsGroupSet.forEach((parentId) => {
      const desc = collectDescendants(parentId, tagNodeMap).map(toIdString);
      desc.forEach((d) => {
        if (d !== parentId) descendantsOfGroupParents.add(d);
      });
    });

    const expandedOrdered: string[] = [];
    pendingIds.forEach((id) => {
      if (descendantsOfGroupParents.has(id)) return;

      if (parentsGroupSet.has(id)) {
        if (!expandedOrdered.includes(id)) expandedOrdered.push(id);
        return;
      }

      if (parentsIndividualSet.has(id)) {
        const desc = collectDescendants(id, tagNodeMap)
          .map(toIdString)
          .filter((d) => d !== id);
        desc.forEach((d) => {
          if (descendantsOfGroupParents.has(d)) return;
          if (!expandedOrdered.includes(d)) expandedOrdered.push(d);
        });
        return;
      }

      if (!expandedOrdered.includes(id)) expandedOrdered.push(id);
    });

    const uniquePending = expandedOrdered.filter(
      (id) => !globalSelected.has(id)
    );
    if (uniquePending.length === 0) {
      setPendingByCategory((prev) => {
        const next = {...prev};
        next[cat] = (prev[cat] ?? []).filter(
          (pid) => !descendantsOfGroupParents.has(toIdString(pid))
        );
        return next;
      });
      setConfirmTagModalOpen(false);
      return;
    }

    const totalSelectedBefore = Object.values(selectedByCategory).reduce(
      (acc, arr) => acc + arr.length,
      0
    );
    const availableSlots = MAX_TOTAL_SELECTED - totalSelectedBefore;
    if (availableSlots <= 0) {
      setIsShowToast(true);
      setConfirmTagModalOpen(false);
      return;
    }

    const toAdd = uniquePending.slice(0, availableSlots);
    const remainingExpanded = uniquePending.slice(availableSlots);

    const maxSeq = getGlobalMaxSequence(selectedByCategory);
    let cur = maxSeq;
    const addedItems: (Items & {__category: CategoryKey})[] = toAdd.map(
      (id) => {
        cur += 1;
        const label = labelMaps.get(cat)?.get(id) ?? id;
        return {
          id,
          label,
          sequence: cur,
          __category: cat,
          displayId: makeDisplayId(id, cat),
        } as Items & {__category: CategoryKey; displayId?: string};
      }
    );

    const currentGlobal = (
      Object.keys(selectedByCategory) as CategoryKey[]
    ).flatMap((c) =>
      (selectedByCategory[c] ?? []).map(
        (it) =>
          ({
            ...it,
            __category: c,
          }) as Items & {__category: CategoryKey}
      )
    );
    const combined = currentGlobal.concat(addedItems);
    const rebuilt = rebuildBucketsFromGlobal(combined);

    setSelectedByCategory(rebuilt);
    seqRef.current = cur;

    const addedSet = new Set(toAdd);
    const excludedByGroupSet = new Set(
      Array.from(descendantsOfGroupParents).filter((d) =>
        pendingIds.includes(d)
      )
    );

    setPendingByCategory((prev) => {
      const old = prev[cat] ?? [];
      const nextList = old.filter((pid) => {
        const key = toIdString(pid);
        if (addedSet.has(key)) return false;
        if (excludedByGroupSet.has(key)) return false;
        return true;
      });
      return {...prev, [cat]: nextList};
    });

    if (remainingExpanded.length > 0) setIsShowToast(true);
    setConfirmTagModalOpen(false);
  }, [
    confirmModalPendingCategory,
    confirmModalParentOptions,
    pendingByCategory,
    selectedByCategory,
    tagNodeMap,
    collectDescendants,
    labelMaps,
    MAX_TOTAL_SELECTED,
    setSelectedByCategory,
    setPendingByCategory,
    setIsShowToast,
    setConfirmTagModalOpen,
    seqRef,
  ]);

  const markedNodesByCategory = useMemo(() => {
    const mark = (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
      group: CategoryKey
    ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
      if (!nodes) return [];
      const traverse: any = (arr: TreeViewBaseItem<ExtendedTreeItemProps>[]) =>
        arr.map((node) => {
          const nodeId = node.id?.toString() ?? '';
          const isLeaf = !node.children || node.children.length === 0;
          const shouldDisableLeaf =
            isLeaf &&
            selectedByCategory[group].some(
              (item) => item.id.toString() === nodeId
            );

          let children;
          if (node.children) children = traverse(node.children);

          let shouldDisableParent = false;

          if (group === 'score') {
            const isParentAdded =
              !isLeaf &&
              selectedByCategory[group]?.some(
                (item) => item.id.toString() === nodeId
              );
            shouldDisableParent = isParentAdded;
          } else {
            const allChildrenDisabled =
              children &&
              children.length > 0 &&
              children.every((c: any) => c.disabled === true);

            shouldDisableParent = allChildrenDisabled;
          }

          return {
            ...node,
            disabled:
              (node.disabled ?? false) ||
              (isLeaf ? shouldDisableLeaf : shouldDisableParent),
            children,
          };
        });
      return traverse(nodes);
    };

    return {
      metadata: mark(metadataList, 'metadata'),
      production: mark(productionList, 'production'),
      tags: mark(tagTree, 'tags'),
      redactionTags: mark(redactionTree, 'redactionTags'),
      commentType: mark(commentTypeTree, 'commentType'),
      folders: mark(folderTree, 'folders'),
      score: mark(scoreTree, 'score'),
      ai: mark(aiTree, 'ai'),
    } as Record<CategoryKey, TreeViewBaseItem<ExtendedTreeItemProps>[]>;
  }, [
    metadataList,
    productionList,
    tagTree,
    redactionTree,
    commentTypeTree,
    folderTree,
    scoreTree,
    selectedByCategory,
  ]);

  const handleItemRemovedFromSelected = useCallback(
    (idRaw: string) => {
      const id = toIdString(idRaw);
      setSelectedByCategory((prev) => {
        const next: Record<CategoryKey, Items[]> = {...prev};
        (Object.keys(next) as CategoryKey[]).forEach((c) => {
          next[c] = next[c].filter((it) => toIdString(it.id) !== id);
        });
        return next;
      });

      setPendingByCategory((prev) => {
        const next = {...prev};
        (Object.keys(next) as CategoryKey[]).forEach((c) => {
          next[c] = next[c].filter((pid) => toIdString(pid) !== id);
        });
        return next;
      });
    },
    [setSelectedByCategory, setPendingByCategory]
  );

  const handleReorder = useCallback(
    (newOrderedItems: Items[]) => {
      let cur = 0;
      const renumbered = newOrderedItems.map((it) => {
        cur += 1;
        return {...it, sequence: cur};
      });

      const idToCat = new Map<string, CategoryKey>();
      (Object.keys(selectedByCategory) as CategoryKey[]).forEach((cat) => {
        selectedByCategory[cat].forEach((it) =>
          idToCat.set(toIdString(it.id), cat)
        );
      });

      const newBuckets: Record<CategoryKey, Items[]> = {
        metadata: [],
        production: [],
        tags: [],
        redactionTags: [],
        commentType: [],
        folders: [],
        score: [],
        ai: [],
      };

      renumbered.forEach((it) => {
        const cat = idToCat.get(toIdString(it.id)) ?? 'metadata';
        newBuckets[cat].push(it);
      });

      setSelectedByCategory(newBuckets);

      seqRef.current = cur;
    },
    [selectedByCategory]
  );

  const defaultExpandedIdsByCategory = useMemo(() => {
    const getAllIds = (nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
      const ids: string[] = [];
      const walk = (arr: TreeViewBaseItem<ExtendedTreeItemProps>[] = []) => {
        arr.forEach((n) => {
          ids.push(n.id?.toString() ?? '');
          if (n.children && n.children.length) walk(n.children);
        });
      };
      walk(nodes);
      return ids;
    };

    return {
      metadata: getAllIds(metadataList),
      production: getAllIds(productionList),
      tags: getAllIds(tagTree),
      redactionTags: getAllIds(redactionTree),
      commentType: getAllIds(commentTypeTree),
      folders: getAllIds(folderTree),
      score: getAllIds(scoreTree),
      ai: getAllIds(aiTree),
    } as Record<CategoryKey, string[]>;
  }, [
    metadataList,
    productionList,
    tagTree,
    redactionTree,
    commentTypeTree,
    folderTree,
    scoreTree,
  ]);

  const aggregatedSelected = useMemo(() => {
    const all = (Object.keys(selectedByCategory) as CategoryKey[]).flatMap(
      (c) => (selectedByCategory[c] ?? []).map((it) => ({...it, __category: c}))
    );
    all.sort(
      (a, b) =>
        Number(a.sequence ?? Number.MAX_SAFE_INTEGER) -
        Number(b.sequence ?? Number.MAX_SAFE_INTEGER)
    );
    return all;
  }, [selectedByCategory]);

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, val: number) => setCurrentTab(val),
    []
  );
  const hideToast = useCallback(() => setIsShowToast(false), []);
  const closePanel = useCallback(
    () => dispatch(setShowManageColumns(false)),
    [dispatch]
  );

  const extractSuffix = (id: string) => {
    const lastDash = id.lastIndexOf('-');
    if (lastDash === -1) return null;

    if (lastDash > 0 && id[lastDash - 1] === '-') {
      return id.substring(lastDash);
    }

    return id.substring(lastDash + 1);
  };

  const formatSelectedForSave = useCallback(
    (items: Items[]) => {
      return items.map((it, index) => {
        const id = it.id?.toString() ?? '';
        const sequence = it.sequence ?? index + 1;
        const fieldType = inferCategoryFromId(id);
        let FieldID = extractSuffix(id);
        let FieldIDApi = id;
        const FieldName = it.label ?? '';
        const FieldLabel = it.label ?? '';
        let FieldDataTye = '';
        if (fieldType == 'metadata') {
          const meta = metadataList.find((item) => item.id == id);
          if (meta) {
            FieldIDApi = id.replace('FIELD', 'M');
            FieldDataTye = meta.dataType;
            FieldID = id.split('FIELD-')[1];
          }
        } else if (fieldType == 'production') {
          const meta = productionList.find((item) => item.id == id);
          if (meta) {
            FieldIDApi = id.replace('PRODUCTION', 'P');
            FieldDataTye = meta.dataType;
            FieldID = id.split('PRODUCTION-')[1];
          }
        } else {
          const _break = id.split('-');
          if (fieldType === 'ai') {
            FieldIDApi = `AIGEN--${_break[2]}`;
          } else {
            FieldIDApi = _break[0][0] + `-` + _break[1];
          }
          FieldDataTye = 'NVARCHAR';
        }
        return {
          FieldID: Number(FieldID?.replace(/[^0-9-]/g, '')),
          FieldIDApi,
          FieldName,
          FieldLabel,
          FieldType: id.split('-')[0],
          SequenceNumber: sequence,
          FieldDataTye,
        };
      });
    },
    [metadataList, productionList]
  );

  const handleSaveClick = useCallback(() => {
    const payload = formatSelectedForSave(aggregatedSelected);
    if (onSave) onSave(payload);
    closePanel();
  }, [formatSelectedForSave, aggregatedSelected, onSave, closePanel]);

  const handleRemoveParentOption = useCallback(
    (parentIdRaw: string) => {
      const parentId = toIdString(parentIdRaw);

      const descendants = collectDescendants(parentId, tagNodeMap).map(
        toIdString
      );
      const removeSet = new Set<string>(descendants);
      removeSet.add(parentId);

      setConfirmModalParentOptions((prev) => {
        const next = prev.filter((p) => p.id !== parentId);
        if (next.length === 0) {
          setConfirmTagModalOpen(false);
        }
        return next;
      });

      setPendingByCategory((prev) => {
        const next = {...prev};
        const old = prev['tags'] ?? [];
        next['tags'] = old.filter((pid) => !removeSet.has(toIdString(pid)));
        return next;
      });
    },
    [collectDescendants, tagNodeMap, toIdString]
  );

  useEffect(() => {
    const root = tabsRef.current;
    if (!root) return;

    const tabs = Array.from(root.querySelectorAll<HTMLElement>('[role="tab"]'));
    tabs.forEach((tab) => {
      tab.setAttribute('tabindex', '0');
    });
  }, [currentTab]);

  useEffect(() => {
    const scoreTabIndex = categories.findIndex((cat) => cat.key === 'score');
    if (currentTab !== scoreTabIndex) return;

    const root = availableContentRef.current;
    if (!root) return;

    const infoIcon = root.querySelector<HTMLElement>(
      '[data-testid="arr-info-icon-conceptTitle"]'
    );

    if (!infoIcon) return;

    const onInfoIconKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();

      requestAnimationFrame(() => {
        infoIcon.dispatchEvent(
          new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
          })
        );
      });
    };

    infoIcon.addEventListener('keydown', onInfoIconKeyDown, true);

    return () => {
      infoIcon.removeEventListener('keydown', onInfoIconKeyDown, true);
    };
  }, [currentTab, categories]);

  const handleTabKeyDown = (
    event: React.KeyboardEvent,
    idx: number,
    categoryKey: CategoryKey,
    hasAvailableItems: boolean
  ) => {
    if (event.key === 'Enter' && currentTab === idx) {
      event.preventDefault();

      requestAnimationFrame(() => {
        if (availableHeadingRef.current) {
          availableHeadingRef.current.focus();
          return;
        }

        if (categoryKey === 'score') {
          const focusedAccordion = focusScoreAccordionTrigger();
          if (focusedAccordion) return;
        }

        if (hasAvailableItems) {
          const focusedItem = focusFirstAvailableInteractiveElement();
          if (focusedItem) return;
        }

        selectedHeadingRef.current?.focus();
      });
    }
  };

  const handleAvailableContentKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>, pendingCount: number) => {
      if (event.key === 'Tab' && !event.shiftKey && pendingCount > 0) {
        event.preventDefault();
        addToSelectedRef.current?.focus();
      }
    },
    []
  );

  const handleAddButtonKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Tab' && !event.shiftKey) {
        event.preventDefault();
        selectedHeadingRef.current?.focus();
      }
    },
    []
  );

  const handleAddToSelectedClick = useCallback(
    (category: CategoryKey) => {
      addPendingToSelected(category);
      requestAnimationFrame(() => {
        selectedHeadingRef.current?.focus();
      });
    },
    [addPendingToSelected]
  );

  const focusFirstAvailableInteractiveElement = useCallback(() => {
    const root = availableContentRef.current;
    if (!root) return false;

    const firstFocusable = root.querySelector<HTMLElement>(
      [
        'button:not([disabled])',
        'input[type="checkbox"]:not([disabled])',
        '[role="checkbox"]',
        '[role="treeitem"]',
        '[role="option"]',
        '[role="listitem"]',
        '[tabindex="0"]',
        'a[href]',
      ].join(', ')
    );

    if (firstFocusable) {
      firstFocusable.focus();
      return true;
    }

    return false;
  }, []);

  const focusScoreAccordionTrigger = useCallback(() => {
    const root = availableContentRef.current;
    if (!root) return false;

    const accordionTrigger = root.querySelector<HTMLElement>(
      [
        'button:not([disabled])',
        '[role="button"]',
        '[aria-expanded]',
        '[tabindex="0"]',
      ].join(', ')
    );

    if (accordionTrigger) {
      accordionTrigger.focus();
      return true;
    }

    return false;
  }, []);

  return (
    <div className="manage-columns" data-testid="manage-columns">
      <Box className="header-bar">
        <div
          className="title-container d-flex display-between"
          data-testid={`manage-columns-title`}
        >
          <Typography
            className="typo-text headline3_size text-color"
            data-testid={`manage-columns-title-text`}
          >
            {t('docList.manageColumn.title')}
          </Typography>
          {!confirmTagModalOpen && (
            <div className="d-flex" data-testid={`manage-columns-cancel-save`}>
              <ArrButton
                variant="secondary"
                size="medium"
                onClick={closePanel}
                dataTestId="manage-columns-cancel-button"
              >
                {t('common.cancel')}
              </ArrButton>
              <ArrButton
                variant="primary"
                size="medium"
                className="ml"
                onClick={handleSaveClick}
                disabled={Object.values(selectedByCategory).every(
                  (a) => a.length === 0
                )}
                dataTestId="manage-columns-save-button"
              >
                {t('common.save')}
              </ArrButton>
            </div>
          )}
        </div>
      </Box>
      {isShowToast && (
        <ArrToastNotification
          message={t('docList.manageColumn.max10Column')}
          status={[ToastType.Warning]}
          onClose={hideToast}
        />
      )}
      {confirmTagModalOpen ? (
        <Box className="tag-drawer">
          <Box className="tag-drawer-header">
            <Box className="tag-drawer-title">
              <Typography
                className="typo-text subhead2_size text-color "
                data-testid={`manage-columns-tags-heading`}
              >
                {t('docList.manageColumn.tagsHeading')} (
                {confirmModalParentOptions.length})
              </Typography>
              <Typography
                className="typo-text body3_size text-color"
                data-testid={`manage-columns-tags-subhead`}
              >
                {t('docList.manageColumn.tagsSubhead')}
              </Typography>
            </Box>
            {confirmModalParentOptions.map((opt, index) => (
              <Box className="tag-drawer-title" key={opt.id}>
                <Box className="tag-drawer-group">
                  <Typography
                    className="typo-text subhead5_size text-color"
                    data-testid={`manage-columns-label-${index}`}
                  >
                    {opt.label}
                  </Typography>
                  <ArrButton
                    title="Remove"
                    aria-label="Remove"
                    variant="secondary"
                    size="small"
                    onClick={() => handleRemoveParentOption(opt.id)}
                    dataTestId="manage-columns-remove-button"
                  >
                    {t('common.remove')}
                  </ArrButton>
                </Box>
                <Box>
                  <ArrRadio
                    dataTestId="tags"
                    name="tags"
                    size="small"
                    value={opt.choice}
                    className="radio-group-control"
                    classNameFormControlLabel="typo-text body3_size text-color"
                    onChange={(e) => {
                      const val = e as 'group' | 'individual';
                      setConfirmModalParentOptions((prev) =>
                        prev.map((p) =>
                          p.id === opt.id ? {...p, choice: val} : p
                        )
                      );
                    }}
                    data={[
                      {
                        label: `${t('docList.manageColumn.groupLabel')}`,
                        value: 'group',
                      },
                      {
                        label: `${t('docList.manageColumn.individualLabel')}`,
                        value: 'individual',
                      },
                    ]}
                  />
                </Box>
              </Box>
            ))}
          </Box>
          <Box className="tag-buttons">
            <Box className="button-group">
              <ArrButton
                title={t('common.cancel')}
                aria-label={t('common.cancel')}
                variant="secondary"
                size="medium"
                onClick={() => setConfirmTagModalOpen(false)}
                dataTestId="manage-columns-tag-cancel-button"
              >
                {t('common.cancel')}
              </ArrButton>

              <ArrButton
                title={t('common.add')}
                aria-label={t('common.add')}
                variant="primary"
                size="medium"
                dataTestId="manage-columns-tag-add-button"
                onClick={() => handleConfirmTagModalPerParent()}
              >
                {t('common.add')}
              </ArrButton>
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          <Box className="tab-headings content" data-testid="tab-list">
            <Tabs
              ref={tabsRef}
              value={currentTab}
              onChange={handleTabChange}
              aria-label="basic tabs"
              className="object-tabs"
              data-testid="tab-reation"
            >
              {categories.map((c, i) => (
                <Tab
                  key={c.key}
                  label={c.label}
                  iconPosition={'end'}
                  value={i}
                  {...a11yProps(i)}
                  className="object-tab"
                  icon={c.icon}
                  onKeyDown={(e) =>
                    handleTabKeyDown(
                      e,
                      i,
                      categories[i].key,
                      (markedNodesByCategory[categories[i].key] ?? []).length >
                        0
                    )
                  }
                />
              ))}
            </Tabs>
          </Box>

          {isLoading ? (
            <ArrLoadingSpinner />
          ) : (
            <Box className="available-group">
              <Box className="available-group-body">
                {categories.map((c, idx) => {
                  const isActive = currentTab === idx;
                  const pending = pendingByCategory[c.key] ?? [];
                  const pendingCount = pending.length;
                  const treeItems = markedNodesByCategory[c.key] ?? [];

                  return (
                    <Box
                      key={c.key}
                      role="tabpanel"
                      id={`simple-tabpanel-${idx}`}
                      aria-labelledby={`simple-tab-${idx}`}
                      hidden={!isActive}
                      className="tabContent"
                    >
                      {isActive && (
                        <Box className="dragdrop-list">
                          <div
                            className="title-container d-flex display-between"
                            data-testid={`manage-columns-available-add-to-selected-${idx}`}
                          >
                            <Typography
                              ref={availableHeadingRef}
                              tabIndex={-1}
                              role="heading"
                              aria-level={2}
                              className="typo-text subhead4_size text box-sub-heading"
                              data-testid={`manage-columns-available`}
                            >
                              {t('docList.manageColumn.available')}
                            </Typography>
                            <ArrButton
                              ref={addToSelectedRef}
                              tabIndex={pendingCount > 0 ? 0 : -1}
                              variant="primary"
                              size="small"
                              onClick={() => handleAddToSelectedClick(c.key)}
                              onKeyDown={handleAddButtonKeyDown}
                              disabled={pendingCount === 0}
                              dataTestId={`manage-columns-add-to-selected-button-${idx}`}
                            >
                              {t('docList.manageColumn.addToSelected')}
                            </ArrButton>
                          </div>

                          <Box
                            ref={isActive ? availableContentRef : null}
                            className="arr-drawer__content scrollable"
                            onKeyDownCapture={(e) => {
                              handleAvailableContentKeyDown(e, pendingCount);
                            }}
                          >
                            {c.key == 'score' ? (
                              <ArrAccordion
                                dataTestId="manage-columns"
                                sections={[
                                  {
                                    id: 'conceptTitle',
                                    title: t(
                                      'docList.manageColumn.conceptTitle'
                                    ),
                                    showInfoIcon: true,
                                    tooltipText: t(
                                      'docList.manageColumn.conceptTooltip'
                                    ),
                                    toolTipTrigger: 'click',
                                    content: (
                                      <>
                                        <ArrTreeView
                                          selectedItem=""
                                          itemsList={treeItems}
                                          handleNoResultMessage={t(
                                            'common.noResults'
                                          )}
                                          allowGlobalSelect={false}
                                          treeSelectedItems={[
                                            ...pending,
                                            ...selectedByCategory[c.key].map(
                                              (it) => it.id.toString()
                                            ),
                                          ]}
                                          multipleSelection
                                          isShowCheckbox
                                          showActions={false}
                                          customContentHeight={36}
                                          setSelectedItem={handleTreeSelection(
                                            c.key
                                          )}
                                          isShowFileType={false}
                                          includeDisabledInSelectionState={true}
                                          defaultExpandedIds={
                                            defaultExpandedIdsByCategory[c.key]
                                          }
                                          checkboxGroupSelection={false}
                                          parentIndependentSelection={true}
                                        />
                                      </>
                                    ),
                                  },
                                ]}
                              />
                            ) : (
                              <ArrTreeView
                                selectedItem=""
                                itemsList={treeItems}
                                handleNoResultMessage={t('common.noResults')}
                                allowGlobalSelect={false}
                                treeSelectedItems={[
                                  ...pending,
                                  ...selectedByCategory[c.key].map((it) =>
                                    it.id.toString()
                                  ),
                                ]}
                                multipleSelection
                                isShowCheckbox
                                showActions={false}
                                customContentHeight={36}
                                setSelectedItem={handleTreeSelection(c.key)}
                                isShowFileType={false}
                                includeDisabledInSelectionState={true}
                                defaultExpandedIds={
                                  defaultExpandedIdsByCategory[c.key]
                                }
                              />
                            )}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}

                <DragAndDropSelectedList
                  selectedItems={aggregatedSelected}
                  onReorder={handleReorder}
                  onRemove={handleItemRemovedFromSelected}
                  selectedHeadingRef={selectedHeadingRef}
                  maxColumnsRef={maxColumnsRef}
                />
              </Box>
            </Box>
          )}
        </>
      )}
    </div>
  );
};

export default ManageColumns;
